import { c as _c } from "react/compiler-runtime";
import { IcoDotsHorizontal16 } from '@onefootprint/icons';
import type { ApiNodeData, GraphOperationRemoveNode } from '@onefootprint/request-types/dashboard';
import { Dropdown, IconButton } from '@onefootprint/ui';
import { cx } from 'class-variance-authority';
import { useState } from 'react';
import useSession from '@/hooks/use-session';
import useStore from '../../hooks/use-store';
import AdvanceStuckIncode from './advance-stuck-incode';
import CopyAsRulesItem from './copy-as-rules-item';
import CopyId from './copy-id';
import CopyItem from './copy-item';
import DeleteItem from './delete-item';
import RetryNode from './retry-node';
type NodeActionsProps = {
  data: ApiNodeData;
  id: string;
  canEdit: boolean;
  removeOperation?: GraphOperationRemoveNode;
};
const useGetNodeActionsByGroup = t0 => {
  const $ = _c(9);
  const {
    id,
    data,
    canEdit,
    removeOperation,
    onActionClick
  } = t0;
  const {
    data: session
  } = useSession();
  const {
    data: t1
  } = useStore();
  const {
    traceEntries,
    traceWorkflowId
  } = t1;
  let t2;
  if ($[0] !== canEdit || $[1] !== data || $[2] !== id || $[3] !== onActionClick || $[4] !== removeOperation || $[5] !== session.user?.isFirmEmployee || $[6] !== traceEntries || $[7] !== traceWorkflowId) {
    const groups = {
      copy: [],
      retry: [],
      delete: []
    };
    if (canEdit || session.user?.isFirmEmployee) {
      groups.copy.push(<CopyItem id={id} onClick={onActionClick} />);
    }
    if (session.user?.isFirmEmployee) {
      groups.copy.push(<CopyId id={id} onClick={onActionClick} />);
    }
    if (session.user?.isFirmEmployee && data.kind === "conditional") {
      groups.copy.push(<CopyAsRulesItem data={data} onClick={onActionClick} />);
    }
    if (canEdit && removeOperation) {
      groups.delete.push(<DeleteItem data={data} operation={removeOperation} onClick={onActionClick} />);
    }
    const traceForNode = traceEntries?.find(trace => trace.decisionNodeId === id);
    if (session.user?.isFirmEmployee && traceWorkflowId && traceForNode?.result.signalKind === "retryable_error_proceed") {
      groups.retry.push(<RetryNode id={id} workflowId={traceWorkflowId} onClick={onActionClick} />);
    }
    if (session.user?.isFirmEmployee && traceWorkflowId && data.kind === "identity_document") {
      groups.retry.push(<AdvanceStuckIncode workflowId={traceWorkflowId} onClick={onActionClick} />);
    }
    t2 = Object.entries(groups).map(_temp).filter(_temp2);
    $[0] = canEdit;
    $[1] = data;
    $[2] = id;
    $[3] = onActionClick;
    $[4] = removeOperation;
    $[5] = session.user?.isFirmEmployee;
    $[6] = traceEntries;
    $[7] = traceWorkflowId;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
};
const NodeActions = t0 => {
  const $ = _c(21);
  const {
    id,
    data,
    canEdit,
    removeOperation
  } = t0;
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      setIsDropdownOpen(false);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const handleActionClick = t1;
  let t2;
  if ($[1] !== canEdit || $[2] !== data || $[3] !== id || $[4] !== removeOperation) {
    t2 = {
      id,
      data,
      canEdit,
      removeOperation,
      onActionClick: handleActionClick
    };
    $[1] = canEdit;
    $[2] = data;
    $[3] = id;
    $[4] = removeOperation;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const groups = useGetNodeActionsByGroup(t2);
  if (!groups.length) {
    return null;
  }
  const t3 = isDropdownOpen ? "opacity-100 translate-x-0 pointer-events-auto" : "opacity-0 -translate-x-4 pointer-events-none group-hover:opacity-100 group-hover:translate-x-0 group-hover:pointer-events-auto max-md:opacity-100 max-md:translate-x-0 max-md:pointer-events-auto";
  let t4;
  if ($[6] !== t3) {
    t4 = cx("flex gap-2 mr-8 transition-all duration-200", t3);
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Dropdown.Trigger asChild={true}><IconButton aria-label="Node actions" icon={IcoDotsHorizontal16} size="compact" variant="outline" rounded={true} className="mr-3 my-3" /></Dropdown.Trigger>;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== groups) {
    let t7;
    if ($[11] !== groups.length) {
      t7 = (t8, index) => {
        const {
          key,
          items
        } = t8;
        return <><Dropdown.Group key={key}>{items.map(_temp3)}</Dropdown.Group>{index < groups.length - 1 && <Dropdown.Divider />}</>;
      };
      $[11] = groups.length;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = groups.map(t7);
    $[9] = groups;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[13] !== t6) {
    t7 = <Dropdown.Portal><Dropdown.Content align="start">{t6}</Dropdown.Content></Dropdown.Portal>;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== isDropdownOpen || $[16] !== t7) {
    t8 = <Dropdown.Root open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>{t5}{t7}</Dropdown.Root>;
    $[15] = isDropdownOpen;
    $[16] = t7;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t4 || $[19] !== t8) {
    t9 = <div className={t4}>{t8}</div>;
    $[18] = t4;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
export default NodeActions;
function _temp(t0) {
  const [key, items] = t0;
  return {
    key,
    items
  };
}
function _temp2(t0) {
  const {
    items: items_0
  } = t0;
  return items_0.length > 0;
}
function _temp3(item) {
  return item;
}